import React from 'react';
import { LogOut, User, Search, Menu } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useGalleryStore } from '../../stores/galleryStore';

interface GalleryHeaderProps {
  isMobileSidebarOpen?: boolean;
  setIsMobileSidebarOpen?: (open: boolean) => void;
}

export default function GalleryHeader({ 
  isMobileSidebarOpen: _isMobileSidebarOpen = false, 
  setIsMobileSidebarOpen 
}: GalleryHeaderProps) {
  const { user, logout } = useAuthStore();
  const { collection, searchQuery, setSearchQuery } = useGalleryStore();

  const handleLogout = async () => {
    await logout();
  };

  return (
    <header className="bg-white border-b border-gray-200 px-4 md:px-6 py-3 md:py-4">
      <div className="flex items-center justify-between">
        {/* Left side - Logo, Mobile menu + Collection info */}
        <div className="flex items-center space-x-3 min-w-0 flex-1 md:flex-none">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileSidebarOpen?.(true)}
            className="md:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
          
          {/* ArtVR Logo - Desktop only */}
          <div className="hidden items-center rounded-xl bg-indigo-950 px-3 py-1.5 shadow-sm md:flex">
            <img 
              src="/artvr-logo.svg"
              alt="ArtVR" 
              className="h-8 w-auto"
            />
          </div>
          
          <div className="min-w-0">
            <h1 className="text-lg md:text-xl font-semibold text-gray-900 truncate">
              {collection?.name || 'Gallery'}
            </h1>
            {collection?.description && (
              <span className="text-xs md:text-sm text-gray-500 hidden sm:inline">
                {collection.description}
              </span>
            )}
          </div>
        </div>

        {/* Center - Search (hidden on mobile) */}
        <div className="hidden md:flex flex-1 max-w-lg mx-8">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search artworks or artists..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Right side - User menu */}
        <div className="flex items-center space-x-1 md:space-x-3">
          <div className="hidden sm:flex items-center space-x-2">
            <User className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
            <span className="text-xs md:text-sm text-gray-700 max-w-24 md:max-w-none truncate">
              {user?.displayName}
            </span>
          </div>
          <button
            onClick={handleLogout}
            className="p-1.5 md:p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title="Logout"
          >
            <LogOut className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        </div>
      </div>
    </header>
  );
}
